import React, { type FC } from 'react'
import { type Activity, ActivityAction, ActivityObjectType, ActivityStatus } from '../types'
import {
  createActionIcon,
  createActivityIcon
} from '../../../components/PathwayActivityFeed/components/helperFunctions/getActivityIcon'
import { Badge, Icon } from '@awell-health/design-system'
import { capitalize, isNil } from 'lodash'
import { ActivityReferenceType, ActivityResolution } from '../../../generated/types-orchestration'

interface Props {
  activity: Activity
}

const ActivityDetailsHeader: FC<Props> = ({ activity }) => {
  const nonUserActivityTypes = [
    ActivityObjectType.PluginAction,
    ActivityObjectType.ApiCall,
    ActivityObjectType.EmrReport,
    ActivityObjectType.Decision,
    ActivityObjectType.Timer,
    ActivityObjectType.Agent
  ]

  const hasLabel = !isNil(activity?.label?.id) && activity?.label?.id !== ''

  const alwaysShowStakeholder = [ActivityObjectType.Message, ActivityObjectType.Checklist, ActivityObjectType.Form]
  const stakeholder = activity.indirect_object?.name ?? ''

  const title = activity.object.name ?? ''
  const track = activity.track?.title ?? ''
  const isAgentDelegated =
    activity.object.type !== ActivityObjectType.Agent && activity.reference_type === ActivityReferenceType.Agent
  const step = isAgentDelegated ? activity.container_name?.split('-')[0] : activity.container_name
  const titleSuffix =
    activity.object.type === ActivityObjectType.Agent
      ? activity.action === ActivityAction.Complete
        ? 'agent signed of'
        : 'agent reported for duty'
      : ''

  const getStatusBadgeVariant = (): string => {
    const { status, object, resolution } = activity
    switch (status) {
      case ActivityStatus.Active:
        return object.type === ActivityObjectType.PluginAction ||
          object.type === ActivityObjectType.ApiCall ||
          object.type === ActivityObjectType.EmrReport
          ? 'primary'
          : 'error'
      case ActivityStatus.Canceled:
      case ActivityStatus.Failed:
        return 'default'
      case ActivityStatus.Done:
        return resolution === ActivityResolution.Failure ? 'error' : 'success'
      case ActivityStatus.Expired:
        return 'error'
      default:
        return 'primary'
    }
  }

  const getStatusBadgeText = (): string => {
    if (activity.status === ActivityStatus.Done && activity.resolution === ActivityResolution.Failure) {
      return 'Failed'
    }
    if (activity.status === ActivityStatus.Active) {
      if (nonUserActivityTypes.includes(activity.object.type)) {
        return 'In progress'
      }
      return 'To do'
    }
    return capitalize(activity.status.toLowerCase())
  }

  const getLabelColorClass = (color: string): string => {
    const colorMap = {
      red: 'error',
      teal: 'teal',
      blue: 'primary',
      green: 'success',
      orange: 'warning',
      purple: 'violet'
    }
    return colorMap[color as keyof typeof colorMap]
  }

  const showStakeholder =
    activity.object.type !== ActivityObjectType.Agent &&
    (alwaysShowStakeholder.includes(activity.object.type) || (activity?.stakeholders ?? []).length > 0)

  return (
    <div className="flex flex-col gap-2">
      {hasLabel && (
        <Badge size={null} variant={getLabelColorClass(activity?.label?.color ?? 'green')}>
          {activity?.label?.text}
        </Badge>
      )}
      <div className="flex gap-2">
        {createActivityIcon(activity)}
        <div className="flex-col gap-1 text-slate-600">
          <div className="font-medium text-base">
            {title} {titleSuffix}
          </div>
          <div className="text-sm text-slate-600">
            {track} {step && step.length > 0 ? `| ${step}` : ''}
          </div>
        </div>
      </div>
      {activity.status && activity.object.type !== ActivityObjectType.Agent ? (
        <div className="flex gap-2 items-center my-2">
          <Badge size={null} variant={getStatusBadgeVariant()}>
            {getStatusBadgeText()}
          </Badge>
          {showStakeholder && (
            <div className="text-sm text-slate-600">
              <Icon icon="RiUserLine" size={16} /> {stakeholder?.length > 0 ? stakeholder : 'Anonymous'}
            </div>
          )}
        </div>
      ) : (
        <div className="h-2" />
      )}
    </div>
  )
}

export default ActivityDetailsHeader
